import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import NotFoundBody from '../NotFoundBody';
import StorefrontAPI from 'utilities/storefrontApi';
import { SHELL, MAX, CARD, SUB_INPUT, brandPath, formatMoney } from './styles';
import useBrand from './useBrand';
import StorefrontNav from './StorefrontNav';
import Breadcrumbs from './Breadcrumbs';
import RecentlyViewed from './RecentlyViewed';
import { ShellMessage } from './shellHelpers';

/**
 * Product grid for a brand storefront.
 *
 * Filter bar (search / category / price / sort) lives at the top.
 * Search input is debounced 250ms so we don't fire a request per
 * keystroke. Initial filter state is hydrated from the URL so links
 * like `/shop?category=apparel` land pre-filtered.
 */
export default function StorefrontShop() {
  const { brand, error } = useBrand();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => {
    let initialCategory = '';
    try {
      const sp = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      if (sp) initialCategory = sp.get('category') || '';
    } catch (_) { /* ignore */ }
    return {
      q: '',
      category: initialCategory,
      min_price: '',
      max_price: '',
      sort: 'newest',
    };
  });
  const [searchInput, setSearchInput] = useState('');
  const accent = brand?.brand_color || '#422AFB';

  useEffect(() => {
    if (!brand) return undefined;
    const handle = setTimeout(() => {
      setFilters((f) => ({ ...f, q: searchInput.trim() }));
    }, 250);
    return () => clearTimeout(handle);
  }, [searchInput, brand]);

  useEffect(() => {
    if (!brand) return;
    setLoading(true);
    StorefrontAPI.listProducts(brand._id, filters)
      .then((res) => {
        // Back-compat: server returns `{items, categories}` now, but
        // tolerate an array for older deployments.
        if (Array.isArray(res)) {
          setProducts(res);
          setCategories([]);
        } else {
          setProducts(res?.items || []);
          setCategories(res?.categories || []);
        }
      })
      .catch(() => { setProducts([]); setCategories([]); })
      .finally(() => setLoading(false));
  }, [brand, filters]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => {
    setFilters({ q: '', category: '', min_price: '', max_price: '', sort: 'newest' });
    setSearchInput('');
  };
  // Genuinely empty, as opposed to filtered down to nothing. Only meaningful
  // once a load has finished, or the first paint would flash the empty state.
  const shopIsEmpty = !loading
    && products.length === 0
    && !filters.q && !filters.category
    && filters.min_price === '' && filters.max_price === ''
    && filters.sort === 'newest';

  const hasActiveFilter =
    !!filters.q || !!filters.category || filters.min_price !== '' || filters.max_price !== '' || filters.sort !== 'newest';

  if (error) return <ShellMessage>{error}</ShellMessage>;
  if (!brand) return <ShellMessage>Loading…</ShellMessage>;

  // A brand with nothing to sell has no shop, so /shop is not a page.
  //
  // It used to render the full storefront — search, category filter, four
  // sort options — above the line "this brand doesn't have any products
  // listed yet". That is a shop that exists and is empty, which is a
  // different and worse impression than an address that was never set up.
  // Anyone linking to /shop from a menu now gets the same treatment as any
  // other wrong address.
  if (shopIsEmpty) {
    return (
      <div style={SHELL}>
        <StorefrontNav brand={brand} />
        <NotFoundBody homePath={brandPath(brand, '')} />
      </div>
    );
  }

  return (
    <div style={SHELL}>
      <StorefrontNav brand={brand} />
      <div style={MAX}>
        <Breadcrumbs
          items={[
            { label: 'Home', to: brandPath(brand, '') },
            { label: 'Shop' },
          ]}
        />
        <h1 style={{ fontSize: 28, margin: '8px 0 16px' }}>Shop</h1>

        {/* No filter bar when the shop is empty. Sorting nothing by price and
            choosing from a list of no categories is furniture, and it makes an
            unfinished shop look broken rather than unfinished. */}
        {!shopIsEmpty && (
        <div style={{ ...CARD, marginBottom: 18, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <input
            placeholder='Search products…'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ ...SUB_INPUT, flex: '1 1 220px', minWidth: 180 }}
          />
          <select
            value={filters.category}
            onChange={(e) => setFilter('category', e.target.value)}
            style={{ ...SUB_INPUT, flex: '0 1 200px' }}
          >
            <option value=''>All categories</option>
            {categories.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name} ({c.count})
              </option>
            ))}
          </select>
          <input
            placeholder='Min price' type='number'
            value={filters.min_price}
            onChange={(e) => setFilter('min_price', e.target.value)}
            style={{ ...SUB_INPUT, width: 110 }}
          />
          <input
            placeholder='Max price' type='number'
            value={filters.max_price}
            onChange={(e) => setFilter('max_price', e.target.value)}
            style={{ ...SUB_INPUT, width: 110 }}
          />
          <select
            value={filters.sort}
            onChange={(e) => setFilter('sort', e.target.value)}
            style={{ ...SUB_INPUT, flex: '0 1 180px' }}
          >
            <option value='newest'>Newest</option>
            <option value='price_asc'>Price: low → high</option>
            <option value='price_desc'>Price: high → low</option>
            <option value='name'>Name (A–Z)</option>
          </select>
          {hasActiveFilter && (
            <button
              type='button'
              onClick={clearFilters}
              style={{
                background: 'transparent', border: 'none', color: accent,
                fontWeight: 600, fontSize: 13, cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
        </div>
        )}

        {!hasActiveFilter && (
          <RecentlyViewed brand={brand} excludeId={null} />
        )}

        {loading && <div style={{ color: '#64748b' }}>Loading…</div>}
        {!loading && products.length === 0 && (
          <div style={{ color: '#64748b' }}>
            {hasActiveFilter
              ? 'No products match your filters.'
              : "This brand doesn't have any products listed yet."}
          </div>
        )}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 18,
        }}>
          {products.map((p) => {
            const img = p.images?.find((i) => i.is_primary) || p.images?.[0];
            return (
              <Link
                key={p._id}
                to={brandPath(brand, `/shop/${p.slug || p._id}`)}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <div style={{ ...CARD, padding: 0, overflow: 'hidden' }}>
                  <div style={{
                    aspectRatio: '1 / 1', background: '#f1f5f9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {img?.url ? (
                      <img src={img.url} alt={img.alt || p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ color: '#94a3b8', fontSize: 12 }}>No image</div>
                    )}
                  </div>
                  <div style={{ padding: 14 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{p.name}</div>
                    {p.category && (
                      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>
                        {p.category}
                      </div>
                    )}
                    <div style={{ fontSize: 14, color: '#0f172a' }}>{formatMoney(p.base_price, p.currency)}</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
